/**
 * SwipeableRow · primitivo reutilizable de "deslizar / accionar" en una fila.
 *
 * Input separado por dispositivo (clave: en desktop el swipe con mouse es
 * frágil — el drag&drop nativo del navegador secuestra el gesto — y poco
 * común como UX):
 *
 *   • TOUCH / PEN → swipe. Arrastrar revela el panel de acción y soltar pasado
 *     el umbral dispara/abre. Blindado contra el drag nativo (draggable=false +
 *     preventDefault del dragstart + select-none).
 *   • MOUSE (Mac/PC) → NO arrastra la fila. En su lugar, un cluster de botones
 *     aparece al pasar el mouse por la fila (y al enfocar por teclado). Botones
 *     reales, directos, sin nada que quede "colgado" a medio abrir.
 *
 *   ┌─────────────────────────────────────────────┐
 *   │ [startAction] ←  contenido  → [endAction]    │  (touch: paneles de swipe)
 *   │                       contenido   [▸ acciones]│  (mouse/teclado: hover)
 *   └─────────────────────────────────────────────┘
 *
 * Accesibilidad: el cluster es la vía de teclado (botones enfocables, visibles
 * con focus-within), así el gesto no es la única forma de accionar. Respeta
 * prefers-reduced-motion.
 *
 * No suma dependencias: Pointer Events nativos + transform/translateX.
 */
import type { LucideIcon } from 'lucide-react';
import { useCallback, useRef, useState, useSyncExternalStore } from 'react';

import { cn } from '@/lib/utils';

/** Ancho mínimo del panel abierto, para que el botón (icono + label) entre. */
const MIN_OPEN_WIDTH = 84;
/** Px extra de arrastre más allá del commit (feedback elástico, sin acción). */
const OVERDRAG_PX = 28;
/** Movimiento mínimo (px) para decidir el eje del gesto (horizontal vs vertical). */
const AXIS_LOCK_PX = 6;

export interface SwipeAction {
  icon: LucideIcon;
  /** Texto accesible + visible del panel revelado / botón de hover. */
  label: string;
  /** Clase de fondo del panel de swipe (ej. 'bg-destructive', 'bg-amber-500/90'). */
  bg: string;
  /** Clase de color del icono/texto del panel de swipe (default 'text-white'). */
  fg?: string;
  onAction: () => void | Promise<void>;
  /** Destructiva (ej. eliminar): tinte rojo en el botón de hover. */
  destructive?: boolean;
}

export interface SwipeableRowProps {
  children: React.ReactNode;
  /** Acción revelada al deslizar hacia la DERECHA (panel a la izquierda). */
  startAction?: SwipeAction;
  /** Acción revelada al deslizar hacia la IZQUIERDA (panel a la derecha). */
  endAction?: SwipeAction;
  /** px a arrastrar para dejar la acción "armada"/abierta (default 72). El ancho
   *  de reposo abierto tiene piso `MIN_OPEN_WIDTH` (84px) para que el botón entre. */
  revealThreshold?: number;
  /** px para que soltar dispare la acción directo (default 132). */
  commitThreshold?: number;
  disabled?: boolean;
  /** Clases del contenedor externo. */
  className?: string;
  /** Clases del contenido que se mueve — necesita fondo OPACO para tapar los
   *  paneles en reposo (default 'bg-background'). En un popover, pasá
   *  'bg-popover'. */
  contentClassName?: string;
  /** Color CSS de fondo del cluster de hover (debe matchear el fondo de la fila
   *  para el fundido lateral). Default 'var(--background)'; en popover pasá
   *  'var(--popover)'. */
  hoverActionBg?: string;
}

/** Suscripción a prefers-reduced-motion sin disparar el lint de setState-in-effect. */
function usePrefersReducedMotion(): boolean {
  return useSyncExternalStore(
    (cb) => {
      const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      mq.addEventListener('change', cb);
      return () => mq.removeEventListener('change', cb);
    },
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    () => false,
  );
}

/** Panel coloreado revelado por el SWIPE (touch). Solo visible al arrastrar. */
function ActionPanel({
  action,
  side,
  onActivate,
}: {
  action: SwipeAction;
  side: 'start' | 'end';
  onActivate: () => void;
}) {
  const Icon = action.icon;
  return (
    <div
      className={cn(
        'absolute inset-y-0 flex w-1/2 items-center',
        // Solo para SWIPE táctil. En dispositivos con hover real (desktop) el
        // gesto está deshabilitado (ver onPointerDown) y la vía es el cluster
        // de hover — el panel coloreado solo se filtraría como "línea" roja/
        // amarilla detrás de la fila. Se oculta ahí.
        '[@media(hover:hover)]:hidden',
        side === 'start' ? 'left-0 justify-start' : 'right-0 justify-end',
        action.bg,
      )}
      aria-hidden
    >
      {/* tabIndex=-1: la vía de teclado es el cluster de hover, no este panel
          (su foco movía el offset y dejaba la "línea de color" colgada). */}
      <button
        type="button"
        tabIndex={-1}
        onClick={onActivate}
        className={cn(
          'flex h-full items-center gap-1.5 px-4 text-micro font-semibold outline-none',
          action.fg ?? 'text-white',
        )}
      >
        <Icon className="size-4 shrink-0" />
        <span>{action.label}</span>
      </button>
    </div>
  );
}

/** Botón del cluster de hover/teclado (desktop). Sobrio, sin adornos. */
function HoverActionButton({ action }: { action: SwipeAction }) {
  const Icon = action.icon;
  return (
    <button
      type="button"
      onClick={() => void action.onAction()}
      aria-label={action.label}
      title={action.label}
      className={cn(
        'flex items-center gap-1.5 rounded-md px-2 py-1 text-micro font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset',
        action.destructive
          ? 'text-destructive hover:bg-destructive/10 focus-visible:ring-destructive/40'
          : 'text-fg-muted hover:text-foreground hover:bg-surface-hi focus-visible:ring-ring/40',
      )}
    >
      <Icon className="size-3.5 shrink-0" />
      <span>{action.label}</span>
    </button>
  );
}

export function SwipeableRow({
  children,
  startAction,
  endAction,
  revealThreshold = 72,
  commitThreshold = 132,
  disabled = false,
  className,
  contentClassName,
  hoverActionBg = 'var(--background)',
}: SwipeableRowProps) {
  const reduceMotion = usePrefersReducedMotion();
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);

  // Refs de gesto (no provocan re-render durante el arrastre).
  const offsetRef = useRef(0);
  const baseOffset = useRef(0);
  const rawStart = useRef({ x: 0, y: 0 });
  const axis = useRef<'none' | 'x' | 'y'>('none');
  const didDrag = useRef(false);
  const pointerId = useRef<number | null>(null);

  const hasSwipe = !disabled && (!!startAction || !!endAction);
  const openWidth = Math.max(revealThreshold, MIN_OPEN_WIDTH);
  const maxDrag = commitThreshold + OVERDRAG_PX;

  const apply = useCallback((next: number) => {
    offsetRef.current = next;
    setOffset(next);
  }, []);

  const fire = useCallback(
    (side: 'start' | 'end') => {
      const action = side === 'start' ? startAction : endAction;
      if (!action) return;
      apply(0);
      void action.onAction();
    },
    [startAction, endAction, apply],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Mouse → NO arrastra (usa el cluster de hover). Solo touch/pen swipean.
      if (!hasSwipe || e.button !== 0 || e.pointerType === 'mouse') return;
      baseOffset.current = offsetRef.current;
      rawStart.current = { x: e.clientX, y: e.clientY };
      axis.current = 'none';
      didDrag.current = false;
      pointerId.current = e.pointerId;
      setDragging(true);
    },
    [hasSwipe],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (pointerId.current !== e.pointerId) return;
      const dx = e.clientX - rawStart.current.x;
      const dy = e.clientY - rawStart.current.y;

      if (axis.current === 'none') {
        if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) return;
        // Si el gesto es más vertical, soltamos: deja scrollear la lista.
        if (Math.abs(dy) > Math.abs(dx)) {
          axis.current = 'y';
          setDragging(false);
          return;
        }
        axis.current = 'x';
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      }
      if (axis.current !== 'x') return;

      didDrag.current = true;
      let next = baseOffset.current + dx;
      if (next > 0 && !startAction) next = 0;
      if (next < 0 && !endAction) next = 0;
      next = Math.max(-maxDrag, Math.min(maxDrag, next));
      apply(next);
    },
    [startAction, endAction, maxDrag, apply],
  );

  const endGesture = useCallback(() => {
    setDragging(false);
    if (axis.current !== 'x') {
      axis.current = 'none';
      return;
    }
    axis.current = 'none';
    const o = offsetRef.current;
    const side: 'start' | 'end' = o > 0 ? 'start' : 'end';
    const action = side === 'start' ? startAction : endAction;
    const mag = Math.abs(o);
    if (action && mag >= commitThreshold) {
      fire(side);
    } else if (action && mag >= revealThreshold) {
      apply(side === 'start' ? openWidth : -openWidth);
    } else {
      apply(0);
    }
  }, [startAction, endAction, commitThreshold, revealThreshold, openWidth, fire, apply]);

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (pointerId.current !== e.pointerId) return;
      pointerId.current = null;
      endGesture();
    },
    [endGesture],
  );

  // Cancelar la navegación/click del contenido si hubo arrastre (touch).
  const onClickCapture = useCallback((e: React.MouseEvent) => {
    if (didDrag.current) {
      e.preventDefault();
      e.stopPropagation();
      didDrag.current = false;
    }
  }, []);

  if (!hasSwipe) {
    return <div className={className}>{children}</div>;
  }

  const isOpen = offset !== 0;

  return (
    <div className={cn('group relative overflow-hidden', className)} data-slot="swipeable-row">
      {/* Paneles de SWIPE (touch). En reposo quedan tapados por el contenido opaco. */}
      {startAction && (
        <ActionPanel action={startAction} side="start" onActivate={() => fire('start')} />
      )}
      {endAction && (
        <ActionPanel action={endAction} side="end" onActivate={() => fire('end')} />
      )}

      <div
        data-slot="swipeable-content"
        style={{ transform: `translate3d(${offset}px,0,0)`, touchAction: 'pan-y' }}
        className={cn(
          'relative select-none will-change-transform',
          !dragging && !reduceMotion && 'transition-transform duration-200 ease-out',
          contentClassName ?? 'bg-background',
        )}
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClickCapture={onClickCapture}
        onFocusCapture={isOpen ? () => apply(0) : undefined}
      >
        {children}
      </div>

      {/* Cluster de acciones para MOUSE + TECLADO (desktop). Oculto en reposo;
          aparece al hover (solo dispositivos con hover real, para no quedar
          pegado en touch) o al enfocar por teclado (cualquier dispositivo). */}
      <div
        data-slot="swipeable-hover-actions"
        style={{
          background: `linear-gradient(to left, ${hoverActionBg}, ${hoverActionBg} 65%, transparent)`,
        }}
        className={cn(
          'pointer-events-none absolute inset-y-0 right-0 z-10 flex items-center gap-1 py-1 pl-10 pr-2 opacity-0 transition-opacity duration-150',
          // SOLO al pasar el mouse, y solo en dispositivos con hover real
          // (desktop). NO usamos focus-within: dejaba el cluster pegado tras un
          // click/Tab (quedaba "fijo" aunque sacaras el mouse). En touch la
          // única vía es el swipe.
          '[@media(hover:hover)]:group-hover:pointer-events-auto [@media(hover:hover)]:group-hover:opacity-100',
        )}
      >
        {startAction && <HoverActionButton action={startAction} />}
        {endAction && <HoverActionButton action={endAction} />}
      </div>
    </div>
  );
}
